/* eslint-disable no-return-assign */
import React from 'react'
import PropTypes from 'prop-types'
import { Editor, EditorState, RichUtils, getDefaultKeyBinding, convertToRaw, convertFromRaw, Modifier } from 'draft-js'
import { isJsonString } from '../../../resources/utilities'

import './style.css'
import 'draft-js/dist/Draft.css'

export default class RichTextEditor extends React.Component {
  constructor (props) {
    super(props)
    let content = null
    let editorState
    if (props.value && isJsonString(props.value)) {
      const rawObject = JSON.parse(props.value)
      content = convertFromRaw(rawObject)
      editorState = EditorState.createWithContent(content)
    } else {
      editorState = EditorState.createEmpty()
    }
    this.state = {
      editorState: editorState,
      rawState: undefined
    }
    this.handleKeyCommand = this._handleKeyCommand.bind(this)
    this.mapKeyToEditorCommand = this._mapKeyToEditorCommand.bind(this)
    this.toggleBlockType = this._toggleBlockType.bind(this)
    this.toggleInlineStyle = this._toggleInlineStyle.bind(this)
    this.focus = this.focus.bind(this)
    this.handleChange = this.handleChange.bind(this)
  }

  componentWillMount () {
    if (this.props.value && !isJsonString(this.props.value)) {
      this.insertText(this.props.value)
    }
  }

  insertText (text) {
    const currState = this.state.editorState
    const selection = currState.getSelection()
    const contentState = currState.getCurrentContent()
    const ncs = Modifier.insertText(contentState, selection, text)
    const editorState = EditorState.push(currState, ncs, 'insert-fragment')
    this.setState({editorState})
  }

  handleChange (editorState) {
    const content = editorState.getCurrentContent()
    const rawState = convertToRaw(content)
    this.setState({
      editorState,
      rawState
    }, () => {
      this.props.onChange(JSON.stringify(rawState))
    })
  }

  focus () {
    this.refs.editor.focus()
  }
  _handleKeyCommand (command, editorState) {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      this.handleChange(newState)
      return true
    }
    return false
  }
  _mapKeyToEditorCommand (e) {
    if (e.keyCode === 9 /* TAB */) {
      const newEditorState = RichUtils.onTab(
        e,
        this.state.editorState,
        4 /* maxDepth */
      )
      if (newEditorState !== this.state.editorState) {
        this.handleChange(newEditorState)
      }
      return
    }
    return getDefaultKeyBinding(e)
  }
  _toggleBlockType (blockType) {
    this.handleChange(
      RichUtils.toggleBlockType(
        this.state.editorState,
        blockType
      )
    )
  }
  _toggleInlineStyle (inlineStyle) {
    this.handleChange(
      RichUtils.toggleInlineStyle(
        this.state.editorState,
        inlineStyle
      )
    )
  }
  render () {
    const {editorState} = this.state
    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    let className = 'RichEditor-editor'
    let contentState = editorState.getCurrentContent()
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        className += ' RichEditor-hidePlaceholder'
      }
    }
    // TODO Insert required option to input
    return (
      <div className='RichEditor-root'>
        <div className='col-xs-12 col-sm-6'>
          <BlockStyleControls
            editorState={editorState}
            onToggle={this.toggleBlockType}
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <InlineStyleControls
            editorState={editorState}
            onToggle={this.toggleInlineStyle}
          />
        </div>
        <div className={className} onClick={this.focus}>
          <Editor readOnly={this.props.readOnly}
            blockStyleFn={getBlockStyle}
            editorState={editorState}
            handleKeyCommand={this.handleKeyCommand}
            keyBindingFn={this.mapKeyToEditorCommand}
            onChange={this.handleChange}
            placeholder={this.props.placeholder}
            ref='editor'
            spellCheck
          />
        </div>
      </div>
    )
  }
}

RichTextEditor.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func
}

function getBlockStyle (block) {
  switch (block.getType()) {
    case 'blockquote': return 'RichEditor-blockquote'
    default: return null
  }
}
class StyleButton extends React.Component {
  constructor () {
    super()
    this.onToggle = (e) => {
      e.preventDefault()
      this.props.onToggle(this.props.style)
    }
  }
  render () {
    let className = 'RichEditor-styleButton'
    if (this.props.active) {
      className += ' RichEditor-activeButton'
    }
    return (
      <span className={className} onMouseDown={this.onToggle}>
        {this.props.label}
      </span>
    )
  }
}
const BLOCK_TYPES = [
  {label: '36', style: 'header-one'},
  {label: '30', style: 'header-two'},
  {label: '24', style: 'header-three'},
  {label: '18', style: 'header-four'},
  {label: '14', style: 'header-five'},
  {label: '12', style: 'header-six'},
  {label: '•', style: 'unordered-list-item'}
]
const BlockStyleControls = (props) => {
  const {editorState} = props
  const selection = editorState.getSelection()
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType()
  return (
    <div className='RichEditor-controls'>
      {BLOCK_TYPES.map((type) =>
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      )}
    </div>
  )
}
const INLINE_STYLES = [
  {label: 'Bold', style: 'BOLD'},
  {label: 'Italic', style: 'ITALIC'},
  {label: 'Underline', style: 'UNDERLINE'}
]
const InlineStyleControls = (props) => {
  const currentStyle = props.editorState.getCurrentInlineStyle()

  return (
    <div className='RichEditor-controls'>
      {INLINE_STYLES.map((type) =>
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      )}
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/common/RichTextEditor/RichTextEditor.js